import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../redux/store/store";
import { setTokens } from "../../redux/slice/authSlice";
import { loginAs, enterOrganization } from "../../redux/slice/layoutSlice";
import { useSignupMutation, useGetPublicPlansQuery } from "../../redux/api";
import { validateEmail, validatePhone } from "../../utils/validation";
import type { XRole } from "../../config/module-types";
import { IconLock } from "../../components/icons";
import { COUNTRIES, DEFAULT_COUNTRY_ISO, flagEmoji, getCountry } from "../../data/countries";
import { PhoneInput } from "../../components/PhoneInput";
import { currencySymbol, regionAmount, regionForCountry } from "../../utils/planPricing";

interface SignupProps {
  /** Plan chosen on the landing page (optional). */
  planId?: string;
  /** Return to the public landing page. */
  onBack?: () => void;
  /** Show the Privacy Policy page (optional — omitted, e.g. in embedded contexts, hides the link). */
  onShowPrivacy?: () => void;
  /** Show the Terms of Service page (optional, same rationale as onShowPrivacy). */
  onShowTerms?: () => void;
}

export const Signup = ({ planId, onBack, onShowPrivacy, onShowTerms }: SignupProps = {}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { data: plans = [] } = useGetPublicPlansQuery();
  const [signup, { isLoading }] = useSignupMutation();

  // Custom is sales-only (contact form on the landing page), so it is not a
  // self-serve purchasable plan — only Pro / Pro+ can be bought here.
  const purchasablePlans = plans.filter(
    (p) => (p.code ?? "").toUpperCase() !== "CUSTOM",
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY_ISO);
  const [selectedPlan, setSelectedPlan] = useState(planId ?? "");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Default the plan select once plans load (use the chosen plan, else first).
  useEffect(() => {
    if (!selectedPlan && purchasablePlans.length) {
      setSelectedPlan(
        planId && purchasablePlans.some((p) => p.id === planId)
          ? planId
          : purchasablePlans[0].id,
      );
    }
  }, [purchasablePlans, planId, selectedPlan]);

  // Country picked above drives which price the Plan dropdown shows —
  // India stays in INR as-is, anything else shows the USD price.
  const region = regionForCountry(country);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = "First name is required.";
    if (!lastName.trim()) errors.lastName = "Last name is required.";
    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(phone, true);
    if (phoneError) errors.phone = phoneError;
    if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }
    if (!organizationName.trim()) {
      errors.organizationName = "Organization name is required.";
    }
    if (!selectedPlan) errors.planId = "Please choose a plan.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      const tokens = await signup({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
        organizationName: organizationName.trim(),
        country: getCountry(country).name,
        planId: selectedPlan,
      }).unwrap();

      dispatch(setTokens(tokens));
      dispatch(
        loginAs({
          name: tokens.name || `${firstName} ${lastName}`.trim(),
          email: tokens.email || email,
          role: (tokens.role as XRole) || "owner",
          organizationId: tokens.organizationId ?? undefined,
        }),
      );
      // Brand-new owner has exactly one organization (the one just created) —
      // enter it directly instead of showing the org picker.
      if (tokens.organizationId) {
        dispatch(
          enterOrganization({
            organizationId: tokens.organizationId,
            organizationName: organizationName.trim(),
            role: (tokens.role as XRole) || "owner",
          }),
        );
      }
    } catch (err) {
      setError(
        (err as { message?: string })?.message ?? "Could not create your account.",
      );
    }
  };

  return (
    <div className="login">
      <form className="login__card login__card--wide" onSubmit={handleSubmit}>
        <span className="login__logo">
          <IconLock size={26} />
        </span>
        <h1 className="login__title">Create your account</h1>
        <p className="login__sub">Start managing your inventory in minutes</p>

        <div className="login__row">
          <label
            className={`login__field${fieldErrors.firstName ? " login__field--invalid" : ""}`}
          >
            First name
            <input
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, firstName: "" }));
              }}
              required
            />
            {fieldErrors.firstName ? (
              <span className="field__error">{fieldErrors.firstName}</span>
            ) : null}
          </label>

          <label
            className={`login__field${fieldErrors.lastName ? " login__field--invalid" : ""}`}
          >
            Last name
            <input
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, lastName: "" }));
              }}
              required
            />
            {fieldErrors.lastName ? (
              <span className="field__error">{fieldErrors.lastName}</span>
            ) : null}
          </label>
        </div>

        <div className="login__row">
          <label
            className={`login__field${fieldErrors.email ? " login__field--invalid" : ""}`}
          >
            Work email
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: "" }));
              }}
              required
              autoComplete="email"
            />
            {fieldErrors.email ? (
              <span className="field__error">{fieldErrors.email}</span>
            ) : null}
          </label>

          <label
            className={`login__field${fieldErrors.phone ? " login__field--invalid" : ""}`}
          >
            Phone number
            <PhoneInput
              value={phone}
              onChange={(v) => {
                setPhone(v);
                setFieldErrors((prev) => ({ ...prev, phone: "" }));
              }}
              required
            />
            {fieldErrors.phone ? (
              <span className="field__error">{fieldErrors.phone}</span>
            ) : null}
          </label>
        </div>

        <label
          className={`login__field${fieldErrors.password ? " login__field--invalid" : ""}`}
        >
          Password
          <input
            type="password"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((prev) => ({ ...prev, password: "" }));
            }}
            required
            autoComplete="new-password"
          />
          {fieldErrors.password ? (
            <span className="field__error">{fieldErrors.password}</span>
          ) : null}
        </label>

        <div className="login__row">
          <label
            className={`login__field${fieldErrors.organizationName ? " login__field--invalid" : ""}`}
          >
            Organization name
            <input
              value={organizationName}
              onChange={(e) => {
                setOrganizationName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, organizationName: "" }));
              }}
              placeholder="Acme Inc."
              required
            />
            {fieldErrors.organizationName ? (
              <span className="field__error">{fieldErrors.organizationName}</span>
            ) : null}
          </label>

          <label className="login__field">
            Country
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c.iso2} value={c.iso2}>
                  {flagEmoji(c.iso2)} {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label
          className={`login__field${fieldErrors.planId ? " login__field--invalid" : ""}`}
        >
          Plan
          <select
            value={selectedPlan}
            onChange={(e) => {
              setSelectedPlan(e.target.value);
              setFieldErrors((prev) => ({ ...prev, planId: "" }));
            }}
          >
            {purchasablePlans.length === 0 ? (
              <option value="">Loading plans…</option>
            ) : (
              purchasablePlans.map((p) => {
                const amount = regionAmount(p, region, "monthly");
                const symbol = region === "IN" ? currencySymbol(p.currency) : "$";
                return (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {amount ? ` — ${symbol}${amount.toLocaleString()}/mo` : " — Free"}
                  </option>
                );
              })
            )}
          </select>
          {fieldErrors.planId ? (
            <span className="field__error">{fieldErrors.planId}</span>
          ) : null}
        </label>

        <button
          type="submit"
          className="btn btn--primary login__submit"
          disabled={isLoading}
        >
          {isLoading ? "Creating account…" : "Create account"}
        </button>

        {error ? (
          <p className="login__hint login__hint--error">{error}</p>
        ) : (
          <p className="login__hint">
            Your subscription starts as pending until activated.
          </p>
        )}

        {onShowPrivacy || onShowTerms ? (
          <p className="login__hint">
            By creating an account, you agree to our
            {onShowTerms ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={onShowTerms}
                  style={{ background: "none", border: "none", padding: 0, color: "var(--primary-dark)", cursor: "pointer", textDecoration: "underline" }}
                >
                  Terms of Service
                </button>
              </>
            ) : null}
            {onShowPrivacy && onShowTerms ? " and" : null}
            {onShowPrivacy ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={onShowPrivacy}
                  style={{ background: "none", border: "none", padding: 0, color: "var(--primary-dark)", cursor: "pointer", textDecoration: "underline" }}
                >
                  Privacy Policy
                </button>
              </>
            ) : null}
            .
          </p>
        ) : null}

        {onBack ? (
          <button
            type="button"
            className="btn btn--ghost login__back"
            onClick={onBack}
          >
            ← Back to home
          </button>
        ) : null}
      </form>
    </div>
  );
};

export default Signup;
